import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { presetRange } from "../lib/format";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreIdState] = useState(localStorage.getItem("bs_store"));
  const [month, setMonth] = useState(null);
  const [basis, setBasisState] = useState(localStorage.getItem("bs_basis") || "total");
  const setBasis = (b) => { setBasisState(b); localStorage.setItem("bs_basis", b); };
  const [range, setRangeState] = useState(null);
  const setRange = (r) => { setRangeState(r); setMonth(r.end.slice(0, 7)); };
  const [settings, setSettings] = useState(null);

  const setStoreId = (id) => {
    setStoreIdState(id);
    if (id) localStorage.setItem("bs_store", id);
    else localStorage.removeItem("bs_store");
  };

  const loadContext = useCallback(async (u) => {
    const [st, se] = await Promise.all([api.get("/stores"), api.get("/settings")]);
    setStores(st.data);
    setSettings(se.data);
    setMonth((m) => m || se.data.effective_date.slice(0, 7));
    setRangeState((r) => r || { preset: "this_month", ...presetRange("this_month", se.data.effective_date) });
    const saved = localStorage.getItem("bs_store");
    const ids = st.data.map((s) => s.id);
    if (u.role === "admin") setStoreId(saved === "all" || ids.includes(saved) ? saved : "all");
    else if (u.role === "manager") setStoreId(ids.includes(saved) ? saved : ids.length === 1 ? ids[0] : null);
    else setStoreId(u.store_id);
  }, []);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("bs_token")) return setUser(false);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (!data.must_change_password) await loadContext(data);
    } catch {
      localStorage.removeItem("bs_token");
      setUser(false);
    }
  }, [loadContext]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (employee_id, password) => {
    const { data } = await api.post("/auth/login", { employee_id, password });
    localStorage.setItem("bs_token", data.token);
    setUser(data.user);
    if (!data.user.must_change_password) await loadContext(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("bs_token");
    localStorage.removeItem("bs_store");
    setUser(false);
    setStores([]);
    setStoreIdState(null);
    setMonth(null);
  };

  const reloadStores = async () => setStores((await api.get("/stores")).data);
  const reloadSettings = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setMonth(data.effective_date.slice(0, 7));
  };

  return (
    <AuthCtx.Provider value={{ user, stores, storeId, setStoreId, month, setMonth, basis, setBasis, range, setRange, settings, login, logout, refresh, reloadStores, reloadSettings }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
